import { useMutation } from '@tanstack/react-query';
import useUser from '../global/useUser';
import { uploadFiles } from '../../services/global';
import {
  EmployeeWaiverQuestions,
  getEmployeeWaiverQuestionsSchema,
  GetWaiverRequestResponse,
} from '../../types/waivers';
import EmployeeWaiverPDF from '../../components/waivers/EmployeeWaiverPDF';
import { getStorageUrl } from '../../helpers/global';
import { mergePDFs, renderPDF } from '../../utils/pdf';
import { submitEmployeeWaiver } from '../../services/waivers';
import { postMessageToReactNative } from '../../utils/global/helper';

interface SubmitWaiverOptions {
  waiverRequest: GetWaiverRequestResponse['waiverRequest'] | undefined;
  waiver: GetWaiverRequestResponse['waiver'] | undefined;
  onSuccess?: () => void | Promise<void>;
  onError?: (
    error: Error,
    variables: EmployeeWaiverQuestions,
  ) => void | Promise<void>;
}

function useSubmitEmployeeWaiver({
  waiverRequest,
  waiver,
  onSuccess,
  onError,
}: SubmitWaiverOptions) {
  const { user } = useUser();

  const submitEmployeeWaiverMutation = useMutation({
    mutationFn: async (payload: EmployeeWaiverQuestions) => {
      if (
        !waiverRequest?.id ||
        !waiverRequest.signedFileLink ||
        !waiver ||
        !waiver.employeeSettings?.enabled ||
        !waiver.employeeSettings.options.length ||
        !waiver.business ||
        !user
      )
        throw new Error('Invalid waiver');

      const waiverFormSchema = getEmployeeWaiverQuestionsSchema(
        waiver.employeeSettings.options ?? [],
      );

      const data = (await waiverFormSchema.validate(
        payload,
      )) as EmployeeWaiverQuestions;

      const originalWaiverPdfUrl = await getStorageUrl(
        waiverRequest.signedFileLink,
      );
      if (!originalWaiverPdfUrl)
        throw new Error('Failed to fetch original waiver PDF');

      const originalWaiverPdfBuffer = await fetch(originalWaiverPdfUrl).then(
        res => res.arrayBuffer(),
      );
      if (!originalWaiverPdfBuffer)
        throw new Error('Failed to fetch original waiver PDF');

      const instance = await renderPDF(
        <EmployeeWaiverPDF waiverForm={data} waiver={waiver} user={user} />,
      );
      const pdfBlob = await instance.toBlob();
      const pdfBuffer = await pdfBlob.arrayBuffer();

      const mergedPdfBuffer = await mergePDFs([
        originalWaiverPdfBuffer,
        pdfBuffer,
      ]);
      const pdfLocalUrl = URL.createObjectURL(
        new Blob([mergedPdfBuffer], { type: 'application/pdf' }),
      );

      const tempFileName = `${waiver.business.businessId}-${waiverRequest.appointmentId}`;
      const basePath = `tempImages/${tempFileName}`;

      const uploads = [
        {
          id: 'waiver',
          path: `${basePath}-signed-waiver-artist`,
          url: pdfLocalUrl,
        },
      ];

      const [pdfUrl] = await uploadFiles(uploads, {
        onSuccess: img => {
          const isObjectUrl = new URL(img.url).protocol === 'blob:';
          if (isObjectUrl) URL.revokeObjectURL(img.url);
        },
      });

      // replace signature fields with booleans (true if signature is present)
      const answers = { ...data } as Record<string, string | boolean>;
      waiver.employeeSettings.options.forEach(option => {
        if (option.type === 'SIGNATURE') {
          const value = answers[option.id] as string;
          answers[option.id] = typeof value === 'string' && !!value.length;
        }
      });

      const request = await submitEmployeeWaiver({
        answers,
        waiverRequestId: waiverRequest?.id,
        signedFileLink: pdfUrl,
      });

      postMessageToReactNative('waiver-submitted');

      return request;
    },
    onSuccess,
    onError,
  });

  return {
    submitWaiverMutation: submitEmployeeWaiverMutation,
    variables: submitEmployeeWaiverMutation.variables,
    isPending: submitEmployeeWaiverMutation.isPending,
    isSuccess: submitEmployeeWaiverMutation.isSuccess,
    isError: submitEmployeeWaiverMutation.isError,
    error: submitEmployeeWaiverMutation.error,
  };
}

export default useSubmitEmployeeWaiver;
